import { useState, useEffect } from "react";
import { apiFetch, fmt, canOnClaim, getUser } from "../api";
import { Spinner } from "./ui";  // UI-SWEEP-CLAIM

// Visual conventions:
//  - source='app'  -> note authored in AtticusNW (📝, navy badge)
//  - source='vca'  -> imported from VCA's FileNotes (📥, slate badge)
// is_internal renders as a red "Internal" tag (hidden from client reports).
// is_important renders as a yellow ★ tag.
const sourceIcon  = (s) => s === "vca" ? "📥" : "📝";
const sourceLabel = (s) => s === "vca" ? "VCA" : "App";
const sourceBg    = (s) => s === "vca" ? "#64748b" : "var(--navy)";

export default function DiaryTab({ claimId, claim, onWorkChanged }) {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ body: "", is_internal: false, is_important: false, tags: [] });
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState(null);
  const [tagQ, setTagQ] = useState("");
  const [tagHits, setTagHits] = useState([]);
  const [quickTags, setQuickTags] = useState([]);   // the claim's parties + claimant — one-click tags
  const [timeline, setTimeline] = useState(null);    // open "notes mentioning X" entity timeline

  const user = getUser();
  // Add Note is allowed if the user has EDIT_CLAIM (full) or EDIT_CLAIM_BASIC
  // (Claims Support) on this claim — server is the authoritative gate, this
  // just hides the button when we know the user can't write.
  const canAddNote = claim
    ? (canOnClaim("edit_claim", claim, user) || canOnClaim("edit_claim_basic", claim, user))
    : false;

  const load = () => {
    setLoading(true);
    setErr(null);
    apiFetch(`/claims/${claimId}/diary`)
      .then(d => { setEntries(d || []); setLoading(false); })
      .catch(e => { setErr(String(e.message || e)); setLoading(false); });
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [claimId]);
  // Tagged parties (#1): search companies/contacts to attach as structured note links.
  useEffect(() => {
    if (!tagQ || tagQ.length < 2) { setTagHits([]); return; }
    const t = setTimeout(() => apiFetch(`/directory/search?q=${encodeURIComponent(tagQ)}`).then(d => {
      const hits = [];
      (d.companies || []).slice(0, 6).forEach(c => hits.push({ entity_type: "company", entity_id: c.id, label: c.company_name }));
      (d.contacts || []).slice(0, 6).forEach(c => hits.push({ entity_type: "contact", entity_id: c.id, label: `${c.first_name || ""} ${c.last_name || ""}`.trim() + (c.company_name ? ` · ${c.company_name}` : "") }));
      setTagHits(hits);
    }).catch(() => {}), 220);
    return () => clearTimeout(t);
  }, [tagQ]);
  // Quick-pick (#1): the claim's own parties + claimant, so the common tags need no search.
  useEffect(() => {
    let dead = false;
    Promise.all([
      apiFetch(`/claims/${claimId}/parties`).catch(() => []),
      apiFetch(`/claims/${claimId}/claimant`).catch(() => null),
    ]).then(([parties, clmt]) => {
      if (dead) return;
      const qt = [];
      if (clmt && clmt.materialized && clmt.claimant && clmt.claimant.id) {
        qt.push({ entity_type: "claimant", entity_id: clmt.claimant.id, label: `Claimant: ${clmt.claimant.display_name || "—"}` });
      }
      (parties || []).forEach(p => {
        if (p.company_id) qt.push({ entity_type: "company", entity_id: p.company_id, label: `${p.role}: ${p.company_name || "—"}` });
        else if (p.contact_id) qt.push({ entity_type: "contact", entity_id: p.contact_id, label: `${p.role}: ${`${p.contact_first_name || ""} ${p.contact_last_name || ""}`.trim() || "—"}` });
      });
      const seen = new Set(); const uniq = [];
      qt.forEach(t => { const k = t.entity_type + t.entity_id; if (!seen.has(k)) { seen.add(k); uniq.push(t); } });
      setQuickTags(uniq);
    });
    return () => { dead = true; };
  }, [claimId]);
  const addTag = (h) => { setForm(f => f.tags.some(t => t.entity_type === h.entity_type && t.entity_id === h.entity_id) ? f : { ...f, tags: [...f.tags, h] }); setTagQ(""); setTagHits([]); };
  const removeTag = (h) => setForm(f => ({ ...f, tags: f.tags.filter(t => !(t.entity_type === h.entity_type && t.entity_id === h.entity_id)) }));

  // Delete (Kuhrt 2026-07-07): app-authored notes only; author or full-edit
  // holders. Server is the authoritative gate — this mirrors it for the UI.
  const canDeleteNote = (e) =>
    e.source === "app" &&
    (e.written_by_user_id === (user && user.id) || canOnClaim("edit_claim", claim, user));

  const deleteNote = async (e) => {
    if (!window.confirm("Delete this note? This can't be undone from the UI.")) return;
    setErr(null);
    try {
      await apiFetch(`/claims/${claimId}/diary/${e.id}`, { method: "DELETE" });
      load();
      onWorkChanged && onWorkChanged();
    } catch (ex) {
      setErr(String(ex.message || ex));
    }
  };

  const save = async () => {
    if (!form.body.trim()) return;
    setSaving(true); setErr(null);
    try {
      await apiFetch(`/claims/${claimId}/diary`, {
        method: "POST",
        body: {
          body: form.body,
          is_internal: form.is_internal,
          is_important: form.is_important,
          tags: form.tags.map(t => ({ entity_type: t.entity_type, entity_id: t.entity_id })),
        },
      });
      setForm({ body: "", is_internal: false, is_important: false, tags: [] });
      setShowForm(false);
      load();
      onWorkChanged && onWorkChanged();   // Overview Recent Activity + Feed re-sync
    } catch (e) {
      setErr(String(e.message || e));
    }
    setSaving(false);
  };

  return (
    <div>
      <div className="section-header">
        <span className="section-title">Diary &amp; Activity Log{!loading && entries.length > 0 ? ` (${entries.length})` : ""}</span>
        {canAddNote && (
          <button className="btn btn-blue btn-sm" onClick={() => setShowForm(!showForm)}>
            {showForm ? "Cancel" : "+ Add Note"}
          </button>
        )}
      </div>

      {showForm && canAddNote && (
        <div className="card" style={{ marginBottom: 12 }}>
          <div className="card-body" style={{ padding: 14 }}>
            <div className="form-group" style={{ marginBottom: 10 }}>
              <label>Note *</label>
              <textarea
                value={form.body}
                onChange={e => setForm({ ...form, body: e.target.value })}
                placeholder="Enter note text..."
                rows={4}
                autoFocus
              />
            </div>
            <div style={{ display: "flex", gap: 20, alignItems: "center", marginBottom: 10, flexWrap: "wrap" }}>
              <label style={{ display: "flex", gap: 8, alignItems: "center", fontSize: 13, cursor: "pointer", fontWeight: 400, textTransform: "none", letterSpacing: "normal", color: "var(--text1)" }}>
                <input type="checkbox"
                       style={{ width: 16, height: 16, minWidth: 16, margin: 0, padding: 0, flexShrink: 0, cursor: "pointer" }}
                       checked={form.is_internal}
                       onChange={e => setForm({ ...form, is_internal: e.target.checked })} />
                Internal only (hidden from client-facing reports)
              </label>
              <label style={{ display: "flex", gap: 8, alignItems: "center", fontSize: 13, cursor: "pointer", fontWeight: 400, textTransform: "none", letterSpacing: "normal", color: "var(--text1)" }}>
                <input type="checkbox"
                       style={{ width: 16, height: 16, minWidth: 16, margin: 0, padding: 0, flexShrink: 0, cursor: "pointer" }}
                       checked={form.is_important}
                       onChange={e => setForm({ ...form, is_important: e.target.checked })} />
                Mark as important
              </label>
            </div>
            <div style={{ marginBottom: 10 }}>
              <label style={{ fontSize: 12, color: "var(--text2)" }}>Tag parties (optional)</label>
              {form.tags.length > 0 && (
                <div style={{ display: "flex", flexWrap: "wrap", gap: 6, margin: "4px 0 6px" }}>
                  {form.tags.map(t => (
                    <span key={t.entity_type + t.entity_id} style={{ fontSize: 12, background: "#eef2ff", color: "#3730a3", borderRadius: 12, padding: "2px 8px", display: "inline-flex", gap: 6, alignItems: "center" }}>
                      {t.label}<span style={{ cursor: "pointer", fontWeight: 700 }} onClick={() => removeTag(t)}>×</span>
                    </span>
                  ))}
                </div>
              )}
              {quickTags.length > 0 && (
                <div style={{ display: "flex", flexWrap: "wrap", gap: 5, margin: "2px 0 6px" }}>
                  {quickTags.map(h => {
                    const on = form.tags.some(t => t.entity_type === h.entity_type && t.entity_id === h.entity_id);
                    return <button key={h.entity_type + h.entity_id} type="button" onClick={() => on ? removeTag(h) : addTag(h)}
                      style={{ fontSize: 11, border: "1px solid var(--border)", background: on ? "#3730a3" : "#fff", color: on ? "#fff" : "var(--text2)", borderRadius: 12, padding: "2px 9px", cursor: "pointer" }}>
                      {on ? "✓ " : "+ "}{h.label}</button>;
                  })}
                </div>
              )}
              <div style={{ position: "relative" }}>
                <input value={tagQ} onChange={e => setTagQ(e.target.value)} placeholder="Search companies / contacts to tag…"
                  style={{ width: "100%", padding: "6px 8px", border: "1px solid var(--border)", borderRadius: 5, fontSize: 13, boxSizing: "border-box" }} />
                {tagHits.length > 0 && (
                  <div style={{ position: "absolute", zIndex: 20, background: "#fff", border: "1px solid var(--border)", borderRadius: 6, width: "100%", maxHeight: 200, overflowY: "auto", boxShadow: "0 6px 20px rgba(0,0,0,0.15)" }}>
                    {tagHits.map(h => (
                      <div key={h.entity_type + h.entity_id} onClick={() => addTag(h)} style={{ padding: "6px 10px", cursor: "pointer", fontSize: 13, borderBottom: "1px solid var(--border-light,#f0f0f0)" }}>
                        {h.label} <span style={{ color: "var(--text3)", fontSize: 11 }}>{h.entity_type}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
            {err && <div className="alert-error">{err}</div>}
            <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
              <button className="btn btn-outline btn-sm" onClick={() => { setShowForm(false); setErr(null); }}>Cancel</button>
              <button className="btn btn-primary btn-sm" onClick={save} disabled={saving || !form.body.trim()}>
                {saving ? "Saving..." : "Save Note"}
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="card">
        {loading ? (
          <Spinner label="Loading diary…" />
        ) : err && entries.length === 0 ? (
          <div className="alert-error" style={{ margin: 14 }}>{err}</div>
        ) : entries.length === 0 ? (
          <div className="empty-state">No diary entries yet</div>
        ) : entries.map(e => {
          // LD-DESIGN-012 pattern 6: date-gutter — date/time rail on the left of each entry.
          const dt = e.written_at ? new Date(e.written_at) : null;
          return (
          <div key={e.id} className="diary-entry">
            <div style={{ flexShrink: 0, width: 54, textAlign: "right", paddingTop: 3 }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: "var(--navy)", whiteSpace: "nowrap" }}>
                {dt ? dt.toLocaleDateString("en-US", { month: "short", day: "numeric" }) : "—"}
              </div>
              <div style={{ fontSize: 10, color: "var(--text3)", whiteSpace: "nowrap" }}>
                {dt ? dt.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" }) : ""}
              </div>
            </div>
            <div className="diary-icon" style={{ background: sourceBg(e.source) }}>{sourceIcon(e.source)}</div>
            <div className="diary-content">
              <div className="diary-body" style={{ whiteSpace: "pre-wrap" }}>{e.body}</div>
              <div className="diary-meta">
                <span style={{ fontWeight: 500 }}>{e.author_name || "Unknown"}</span>
                <span>·</span>
                <span style={{ background: sourceBg(e.source), color: "white", padding: "1px 6px", borderRadius: 3, fontSize: 9, fontWeight: 600, letterSpacing: 0.3 }}>{sourceLabel(e.source)}</span>
                {e.is_internal && <span style={{ color: "var(--red)", fontWeight: 600 }}>· Internal</span>}
                {e.is_important && <span style={{ color: "#d97706", fontWeight: 600 }}>· ★ Important</span>}
                {e.is_edited && <span style={{ color: "var(--text3)", fontStyle: "italic" }}>· edited</span>}
                {canDeleteNote(e) && (
                  <button type="button" title="Delete this note"
                    onClick={() => deleteNote(e)}
                    style={{ marginLeft: 6, border: "none", background: "none", color: "var(--red)", fontSize: 11, cursor: "pointer", padding: 0 }}>
                    · delete
                  </button>
                )}
              </div>
              {(e.tags || []).length > 0 && (
                <div style={{ marginTop: 4, display: "flex", flexWrap: "wrap", gap: 5 }}>
                  {e.tags.map(t => <button key={t.entity_type + t.entity_id} type="button" title={`See all notes mentioning ${t.label}`} onClick={() => setTimeline({ entity_type: t.entity_type, entity_id: t.entity_id, label: t.label })} style={{ fontSize: 11, background: "#eef2ff", color: "#3730a3", border: "none", borderRadius: 10, padding: "1px 7px", cursor: "pointer" }}>🏷 {t.label}</button>)}
                </div>
              )}
            </div>
          </div>
        );})}
      </div>
      {timeline && <EntityTimeline entity={timeline} onClose={() => setTimeline(null)} />}
    </div>
  );
}

// #1 entity timeline — "all notes mentioning X" across every claim the viewer can access.
function EntityTimeline({ entity, onClose }) {
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  useEffect(() => {
    apiFetch(`/entities/${entity.entity_type}/${entity.entity_id}/notes`)
      .then(setData).catch(e => setErr(String(e.message || e)));
  }, [entity]);
  return (
    <div onClick={onClose} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.4)", zIndex: 1000, display: "flex", alignItems: "flex-start", justifyContent: "center", overflowY: "auto", padding: "40px 16px" }}>
      <div onClick={e => e.stopPropagation()} className="card" style={{ width: 640, maxWidth: "100%", background: "#fff", padding: 18 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
          <div style={{ fontSize: 15, fontWeight: 700 }}>🏷 Notes mentioning {entity.label || "this party"}</div>
          <button className="btn btn-sm btn-outline" onClick={onClose}>Close</button>
        </div>
        {err && <div className="alert-error">{err}</div>}
        {!data && !err && <Spinner label="Loading timeline…" />}
        {data && (data.notes || []).length === 0 && <div className="empty-state" style={{ padding: 16 }}>No notes tag this party yet.</div>}
        {data && (data.notes || []).map(n => (
          <div key={n.id} style={{ borderTop: "1px solid var(--border)", padding: "8px 0" }}>
            <div style={{ fontSize: 11, color: "var(--text3)", marginBottom: 2 }}>
              <span className="mono" style={{ color: "var(--blue)" }}>{n.claim_number || "—"}</span> · {n.author_name || "Unknown"} · {fmt.datetime(n.written_at)}
              {n.is_internal && <span style={{ color: "var(--red)", fontWeight: 600 }}> · Internal</span>}
              {n.is_important && <span style={{ color: "#d97706", fontWeight: 600 }}> · ★</span>}
            </div>
            <div style={{ fontSize: 13, whiteSpace: "pre-wrap" }}>{n.body}</div>
          </div>
        ))}
        {data && (data.notes || []).length >= 200 && <div style={{ fontSize: 11, color: "var(--text3)", marginTop: 8 }}>Showing the 200 most recent.</div>}
      </div>
    </div>
  );
}
